import React, { useState } from 'react';

interface ChakraBarProps {
  score: number;
  breakdown?: {
    language: string;
    positiveScore: number;
    negativeScore: number;
    culturalMultiplier: number;
    isSarcastic: boolean;
    bonuses?: {
      hasQuestion: boolean;
      hasSpecificNumbers: boolean;
      isEducational: boolean;
    };
    wordMatches?: {
      positive: Array<{
        word: string;
        contribution: number;
        frequency: number;
      }>;
      negative: Array<{
        word: string;
        contribution: number;
        frequency: number;
      }>;
    };
  };
  showDetails?: boolean;
}

/**
 * ChakraBar component - Visual representation of VibeScore
 */
export function ChakraBar({ score, breakdown, showDetails = false }: ChakraBarProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const chakraColor = getChakraColorFromScore(score);
  
  return (
    <div className="w-full relative">
      {/* Main chakra bar with full gradient */}
      <div 
        className="relative h-1 rounded overflow-hidden cursor-pointer"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{
          background: 'linear-gradient(90deg, #E53935 0%, #FB8C00 14.3%, #FDD835 28.6%, #43A047 42.9%, #1E88E5 57.2%, #3949AB 71.5%, #8E24AA 85.8%, #8E24AA 100%)'
        }}
        title={`VibeScore ${score} - ${chakraColor.description}`}
      >
        {/* Overlay to show unfilled portion */}
        <div
          className="absolute right-0 top-0 h-full bg-slate-300 opacity-70 transition-all duration-400 ease-out"
          style={{ width: `${100 - score}%` }}
        />
        
        {/* Score marker */}
        <div
          className="absolute top-0 w-0.5 h-full bg-white shadow-sm transition-all duration-400"
          style={{ left: `${Math.min(score, 98)}%` }}
        />
      </div>
      
      {/* Score display */}
      <div className="flex justify-between items-center mt-1">
        <div className="text-[10px] text-slate-500">
          VibeScore {score}
        </div>
        <div 
          className="text-[10px] font-medium"
          style={{ color: chakraColor.color }}
        >
          {chakraColor.name}
        </div>
      </div>
      
      {/* Tooltip with detailed breakdown */}
      {showTooltip && breakdown && (
        <div className="absolute z-10 mt-2 p-3 bg-white border rounded-lg shadow-lg text-xs max-w-sm">
          <div className="font-semibold mb-2">Waarom deze score?</div>
          
          {/* Language detection */}
          <div className="mb-2">
            <span className="text-slate-600">Taal:</span> 
            <span className="ml-1 font-medium">
              {breakdown.language === 'nl' ? 'Nederlands' : 'English'}
            </span>
          </div>
          
          {/* Score components */}
          <div className="space-y-1 mb-2">
            <div className="flex justify-between">
              <span className="text-green-600">Positieve woorden:</span>
              <span>+{breakdown.positiveScore.toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-600">Negatieve woorden:</span>
              <span>-{breakdown.negativeScore.toFixed(1)}</span>
            </div>
            {breakdown.culturalMultiplier !== 1.0 && (
              <div className="flex justify-between">
                <span className="text-blue-600">Culturele aanpassing:</span>
                <span>×{breakdown.culturalMultiplier.toFixed(2)}</span>
              </div>
            )}
          </div>
          
          {/* Bonuses and penalties */}
          {(breakdown.bonuses?.hasQuestion || breakdown.bonuses?.hasSpecificNumbers || breakdown.bonuses?.isEducational || breakdown.isSarcastic) && (
            <div className="border-t pt-2 mb-2">
              <div className="text-slate-600 text-[10px] mb-1">Aanpassingen:</div>
              {breakdown.bonuses?.hasQuestion && (
                <div className="text-green-600 text-[10px]">✓ Vraag stellen (+5)</div>
              )}
              {breakdown.bonuses?.hasSpecificNumbers && (
                <div className="text-green-600 text-[10px]">✓ Concrete cijfers (+8)</div>
              )}
              {breakdown.bonuses?.isEducational && (
                <div className="text-green-600 text-[10px]">✓ Educatieve waarde (+10)</div>
              )}
              {breakdown.isSarcastic && (
                <div className="text-red-600 text-[10px]">⚠ Sarcasme gedetecteerd (-70%)</div>
              )}
            </div>
          )}
          
          {/* Top contributing words */}
          {breakdown.wordMatches && (
            <div className="border-t pt-2">
              <div className="text-slate-600 text-[10px] mb-1">Top woorden:</div>
              {breakdown.wordMatches.positive.slice(0, 3).map((match, idx) => (
                <div key={idx} className="text-green-600 text-[10px]">
                  +{match.contribution.toFixed(1)} "{match.word}"
                  {match.frequency > 1 && ` (${match.frequency}×)`}
                </div>
              ))}
              {breakdown.wordMatches.negative.slice(0, 2).map((match, idx) => (
                <div key={idx} className="text-red-600 text-[10px]">
                  -{match.contribution.toFixed(1)} "{match.word}"
                  {match.frequency > 1 && ` (${match.frequency}×)`}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Extended breakdown for debugging */}
      {showDetails && breakdown && (
        <div className="mt-4 p-3 bg-gray-50 rounded text-xs">
          <details>
            <summary className="cursor-pointer font-medium">Technische details</summary>
            <pre className="mt-2 text-[10px] overflow-x-auto">
              {JSON.stringify(breakdown, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}

/**
 * Get chakra color information from score
 */
function getChakraColorFromScore(score: number) {
  if (score >= 90) return { color: '#8E24AA', name: 'kroon', description: 'Wijs, voedzaam' };
  if (score >= 75) return { color: '#3949AB', name: 'derde oog', description: 'Inzicht' };
  if (score >= 60) return { color: '#1E88E5', name: 'keel', description: 'Heldere communicatie' };
  if (score >= 45) return { color: '#43A047', name: 'hart', description: 'Hart, verbinding' };
  if (score >= 30) return { color: '#FDD835', name: 'zonnevlecht', description: 'Drive, richting' };
  if (score >= 15) return { color: '#FB8C00', name: 'sacraal', description: 'Creatie, maar onrustig' };
  return { color: '#E53935', name: 'wortel', description: 'Root, mogelijk triggerend' };
}

export default ChakraBar;

















